import React from "react";
import APIUrl from "../../APIUrl";
import Roles from "../../enums/Roles";
import RentalStatus from "../../enums/RentalStatus";
import CustomPagination from "../sub/CustomPagination";
import TruncatedCell from "./TruncatedCell";

class RentalSectionTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
    };
    this.itemsPerPage = 25;

    // Ordre de priorité des statuts pour le tri
    // 1. EN_ATTENTE (0) - À traiter
    // 2. EN_PREPARATION (7) - En préparation
    // 3. A_LIVRER (5) - À livrer
    // 4. A_RECUPERER (6) - À récupérer
    // 5. ATTENTE_FIN (4) - Demande de fin en attente
    // 6. DATE_DEPASSEE (3) - Date dépassée
    // 7. ACTIF (1) - Actif
    // 8. TERMINE (2) - Terminé
    this.statusPriority = {
      [RentalStatus.EN_ATTENTE]: 1,
      [RentalStatus.EN_PREPARATION]: 2,
      [RentalStatus.A_LIVRER]: 3,
      [RentalStatus.A_RECUPERER]: 4,
      [RentalStatus.ATTENTE_FIN]: 5,
      [RentalStatus.DATE_DEPASSEE]: 6,
      [RentalStatus.ACTIF]: 7,
      [RentalStatus.TERMINE]: 8,
    };
  }

  // Trier les locations par statut selon l'ordre de priorité
  sortRentalsByStatus = (rentals) => {
    return [...rentals].sort((a, b) => {
      const priorityA = this.statusPriority[a.status] || 999;
      const priorityB = this.statusPriority[b.status] || 999;
      return priorityA - priorityB;
    });
  };

  componentDidUpdate(prevProps) {
    // Réinitialiser à la page 1 si les rentals changent (filtres, mois, etc.)
    if (prevProps.rentals.length !== this.props.rentals.length) {
      this.setState({ currentPage: 1 });
    }
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  render() {
    const {
      title,
      rentals,
      sectionKey,
      badgeColor,
      isExpanded,
      sectionTotal,
      hideTotalBadge,
      userRole,
      openDropdowns,
      dropdownDirections,
      onToggleSection,
      onOpenLightbox,
      onToggleDropdown,
      onCloseAllDropdowns,
      onModifyRental,
      onOpenValidationModal,
      onOpenDeleteModal,
      onOpenExtendModal,
      onOpenTerminateModal,
      onOpenRecoveryModal,
      onCancelEndRequest,
      onOpenResidentModal,
      onOpenInternalRefModal,
      onOpenPriceModal,
      formatPrice,
      formatStatus,
      calculateMonthlyPrice,
    } = this.props;

    const { currentPage } = this.state;
    const isAdmin = userRole === Roles.ADMIN || userRole === Roles.SALES_REP;

    // Trier les locations par statut avant la pagination
    const sortedRentals = this.sortRentalsByStatus(rentals);

    // Pagination - calculer les rentals à afficher
    const startIndex = (currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const paginatedRentals = sortedRentals.slice(startIndex, endIndex);

    return (
      <div className="card mb-3 rental-section-card-visible">
        <div
          className="card-header d-flex justify-content-between align-items-center rental-section-header-clickable"
          onClick={() => onToggleSection(sectionKey)}
        >
          <h5 className="mb-0">
            <i
              className={`fas fa-chevron-${isExpanded ? "down" : "right"} mr-2`}
            ></i>
            {title}
          </h5>
          <div className="d-flex align-items-center gap-3">
            <span className={`badge badge-${badgeColor} p-2`}>
              {rentals.length} location{rentals.length !== 1 ? "s" : ""}
            </span>
            {!hideTotalBadge && (
              <span className="badge section-total-badge">
                Total: {formatPrice(sectionTotal)}
              </span>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="card-body rental-section-body-visible">
            {rentals.length === 0 ? (
              <div className="text-muted text-center py-3">
                Aucune location dans cette catégorie
              </div>
            ) : (
              <div className="table-responsive rental-table-responsive-visible">
                <table className="table table-sm rental-table">
                  <thead>
                    <tr>
                      <th className="rental-table-th-image">
                        <span className="tooltip-trigger" data-tooltip="Image du produit">Img</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Nom du produit">Produit</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Référence interne">Réf.</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Nom du demandeur">Demandeur</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Date de début de location">Début</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Date de fin de location">Fin</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Statut de la location">Statut</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Commentaire">Comment.</span>
                      </th>
                      <th>
                        <span className="tooltip-trigger" data-tooltip="Répartition de la quantité (Résident / Commun / Tampon)">Affectation</span>
                      </th>
                      <th className="rental-table-th-resident">
                        <span className="tooltip-trigger" data-tooltip="Résident assigné et localisation">Résident</span>
                      </th>
                      {isAdmin && (
                        <th>
                          <span className="tooltip-trigger" data-tooltip="Nom de l'établissement">Établ.</span>
                        </th>
                      )}
                      <th className="text-end">
                        <span className="tooltip-trigger" data-tooltip="Prix journalier hors taxes">Prix J. HT</span>
                      </th>
                      <th className="text-end">
                        <span className="tooltip-trigger" data-tooltip="Prix de la période mensuelle">Prix pér.</span>
                      </th>
                      <th className="rental-table-th-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRentals.map((rental, pageIndex) => {
                      // Calculer l'index global pour les dropdowns
                      const globalIndex = startIndex + pageIndex;
                      return (
                      <tr key={globalIndex}>
                        <td>
                          {rental.product &&
                          (rental.is_dm_rental ||
                            rental.product.mercurial_id ||
                            rental.product_name) ? (
                            <img
                              src={
                                rental.product_name
                                  ? `${APIUrl.getMaterialTypeImg}${rental.material_type_id}/${rental.product_name}/${Date.now()}?token=${APIUrl.jwtToken}`
                                  : rental.is_dm_rental
                                    ? `${APIUrl.getMaterialTypeImg}${rental.product.material_type_id}/${rental.product.material_type}/${Date.now()}?token=${APIUrl.jwtToken}`
                                    : `${APIUrl.getProductImg}${rental.product.mercurial_id}/${rental.product.ref_frn || ""}/${Date.now()}/${rental.product.ref || ""}/?token=${APIUrl.jwtToken}`
                              }
                              alt={rental.product.designation || "Produit"}
                              className="rental-product-img"
                              onClick={() =>
                                onOpenLightbox(
                                  rental.product_name
                                    ? `${APIUrl.getMaterialTypeImg}${rental.material_type_id}/${rental.product_name}/${Date.now()}?token=${APIUrl.jwtToken}`
                                    : rental.is_dm_rental
                                      ? `${APIUrl.getMaterialTypeImg}${rental.product.material_type_id}/${rental.product.material_type}/${Date.now()}?token=${APIUrl.jwtToken}`
                                      : `${APIUrl.getProductImg}${rental.product.mercurial_id}/${rental.product.ref_frn || ""}/${Date.now()}/${rental.product.ref || ""}/?token=${APIUrl.jwtToken}`,
                                )
                              }
                              onError={(e) => {
                                if (
                                  rental.is_dm_rental ||
                                  rental.product_name
                                ) {
                                  e.target.onerror = null;
                                  e.target.src = "/images/no_image_512.png";
                                } else {
                                  e.target.style.display = "none";
                                  e.target.nextElementSibling.style.display =
                                    "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="rental-product-img-placeholder"
                            style={{
                              display:
                                rental.product &&
                                (rental.is_dm_rental ||
                                  rental.product.mercurial_id ||
                                  rental.product_name)
                                  ? "none"
                                  : "flex",
                            }}
                          >
                            <i className="fas fa-image text-muted"></i>
                          </div>
                        </td>
                        <td>
                          {rental.product_name ? (
                            <TruncatedCell
                              text={rental.product_name}
                              maxLength={20}
                              className="rental-product-name"
                            />
                          ) : rental.product && rental.product.designation ? (
                            <TruncatedCell
                              text={rental.product.designation}
                              maxLength={20}
                              className="rental-product-name"
                            />
                          ) : (
                            <span className="rental-product-name-missing">
                              Produit non trouvé
                            </span>
                          )}
                        </td>
                        <td>
                          <TruncatedCell
                            text={
                              rental.ref_interne ||
                              (rental.product && rental.product.ref)
                                ? rental.ref_interne || rental.product.ref
                                : "A compléter"
                            }
                            maxLength={15}
                            className="rental-table-text"
                          />
                        </td>
                        <td>
                          {rental.user && `${rental.user.first_name || ""} ${rental.user.name || ""}`.trim() ? (
                            <TruncatedCell
                              text={`${rental.user.first_name || ""} ${rental.user.name || ""}`.trim()}
                              maxLength={15}
                              className="rental-table-text"
                            />
                          ) : (
                            <span className="rental-empty-value">-</span>
                          )}
                        </td>
                        <td>
                          <div className="rental-table-text">
                            {rental.start_date
                              ? new Date(rental.start_date).toLocaleDateString(
                                  "fr-FR",
                                )
                              : "N/A"}
                          </div>
                        </td>
                        <td>
                          {rental.end_date ? (
                            <div className="rental-table-text">
                              {new Date(rental.end_date).toLocaleDateString("fr-FR")}
                            </div>
                          ) : (
                            <span className="rental-empty-value">-</span>
                          )}
                        </td>
                        <td>
                          {isAdmin ? (
                            <span
                              className={`badge badge-${formatStatus(rental.status).color} px-3 py-2 rental-status-badge`}
                            >
                              {formatStatus(rental.status).text}
                            </span>
                          ) : (
                            <span
                              className={`badge badge-${formatStatus(rental.status).color} px-3 py-2 rental-status-badge-small`}
                            >
                              {formatStatus(rental.status).text}
                            </span>
                          )}
                        </td>
                        <td>
                          <div
                            className={`rental-comment-text ${rental.validation_comment || rental.end_request_comment ? "rental-comment-text-normal" : "rental-comment-text-italic"}`}
                          >
                            {rental.status === RentalStatus.ATTENTE_FIN && rental.end_request_comment ? (
                              <div>
                                <span
                                  className="tooltip-trigger"
                                  data-tooltip={rental.end_request_comment}
                                >
                                  {rental.end_request_comment.length > 50
                                    ? rental.end_request_comment.substring(0, 50) + "..."
                                    : rental.end_request_comment}
                                </span>
                              </div>
                            ) : rental.validation_comment ? (
                              <div>
                                <span
                                  className="tooltip-trigger"
                                  data-tooltip={rental.validation_comment}
                                >
                                  {rental.validation_comment.length > 50
                                    ? rental.validation_comment.substring(
                                        0,
                                        50,
                                      ) + "..."
                                    : rental.validation_comment}
                                </span>
                              </div>
                            ) : (
                              <span className="rental-empty-value">-</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="rental-table-text">
                            {rental.allocation_type ? (
                              <span className="badge badge-secondary">
                                {rental.allocation_type === "resident" ? "Résident" :
                                 rental.allocation_type === "commun" ? "Commun" :
                                 "Tampon"}
                              </span>
                            ) : (
                              <span className="rental-empty-value">-</span>
                            )}
                          </div>
                        </td>
                        <td className="rental-table-td-resident">
                          <div className="rental-resident-info">
                            <span
                              className={`rental-resident-icon tooltip-trigger ${(rental.resident || rental.floor || rental.room) ? '' : 'rental-resident-icon-empty'}`}
                              data-tooltip={
                                (rental.resident || rental.floor || rental.room) ?
                                  ((rental.resident ? `${rental.resident.first_name || ''} ${rental.resident.name || ''}`.trim() : '') +
                                  ((rental.resident && (rental.floor || rental.room)) ? '\n' : '') +
                                  ((rental.floor || rental.room) ?
                                    `${rental.floor ? 'Étage: ' + rental.floor : ''}${rental.floor && rental.room ? ' - ' : ''}${rental.room ? 'Ch: ' + rental.room : ''}`
                                    : ''))
                                  : 'Cliquez pour assigner un résident'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenResidentModal(rental);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className={`fas ${(rental.resident || rental.floor || rental.room) ? 'fa-user-check' : 'fa-user-plus'}`}></i>
                            </span>
                          </div>
                        </td>
                        {isAdmin && (
                          <td>
                            <TruncatedCell
                              text={rental.establishment?.name || "N/A"}
                              maxLength={20}
                              className="rental-table-text"
                            />
                          </td>
                        )}
                        <td className="text-end">
                          <div className="rental-table-text-bold">
                            {rental.daily_price &&
                            !isNaN(parseFloat(rental.daily_price))
                              ? formatPrice(parseFloat(rental.daily_price))
                              : rental.product &&
                                  rental.product.prix_u_ht_emera != null &&
                                  !isNaN(
                                    parseFloat(rental.product.prix_u_ht_emera),
                                  )
                                ? formatPrice(
                                    parseFloat(rental.product.prix_u_ht_emera),
                                  )
                                : "A compléter"}
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="rental-table-text-bold">
                            {formatPrice(calculateMonthlyPrice(rental))}
                          </div>
                        </td>
                        <td>
                          {rental.status === RentalStatus.EN_ATTENTE ||
                          rental.status === RentalStatus.EN_PREPARATION ||
                          rental.status === RentalStatus.A_LIVRER ||
                          rental.status === RentalStatus.ACTIF ||
                          rental.status === RentalStatus.ATTENTE_FIN ||
                          (rental.status === RentalStatus.A_RECUPERER &&
                            (userRole === Roles.ADMIN ||
                              userRole === Roles.SALES_REP)) ||
                          (rental.status === RentalStatus.DATE_DEPASSEE &&
                            (userRole === Roles.ADMIN ||
                              userRole === Roles.SALES_REP)) ||
                          (rental.status === RentalStatus.TERMINE &&
                            (userRole === Roles.ADMIN ||
                              userRole === Roles.SALES_REP)) ? (
                            <div
                              className="rental-action-container"
                              style={{
                                zIndex: openDropdowns[`${sectionKey}-${globalIndex}`]
                                  ? 999999
                                  : 999998,
                              }}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleDropdown(globalIndex, sectionKey, e);
                                }}
                                className="rental-action-button-kebab"
                                id={`action-btn-${sectionKey}-${globalIndex}`}
                                title="Actions"
                              >
                                <i className="fas fa-ellipsis-v"></i>
                              </button>
                              {openDropdowns[`${sectionKey}-${globalIndex}`] &&
                                (() => {
                                  const dropdownId = `${sectionKey}-${globalIndex}`;
                                  const direction =
                                    dropdownDirections[dropdownId] || "down";
                                  const isUp = direction === "up";

                                  return (
                                    <div
                                      className={`rental-action-dropdown ${isUp ? "rental-action-dropdown-top" : "rental-action-dropdown-bottom"}`}
                                      style={{ zIndex: 9999999 }}
                                    >
                                      {(rental.status ===
                                        RentalStatus.EN_ATTENTE ||
                                        rental.status ===
                                          RentalStatus.EN_PREPARATION ||
                                        rental.status ===
                                          RentalStatus.A_LIVRER ||
                                        rental.status ===
                                          RentalStatus.ACTIF) && (
                                        <button
                                          className="action-dropdown-item modify-action"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onModifyRental(rental);
                                            onCloseAllDropdowns();
                                          }}
                                        >
                                          <i className="fas fa-calendar-alt rental-action-dropdown-icon-modify"></i>
                                          <div className="rental-action-dropdown-content">
                                            <span className="rental-action-dropdown-title">
                                              {rental.status === RentalStatus.EN_ATTENTE || rental.status === RentalStatus.EN_PREPARATION || rental.status === RentalStatus.A_LIVRER
                                                ? "Modifier les dates"
                                                : isAdmin
                                                  ? "Modifier les dates"
                                                  : "Demander la fin de la location"}
                                            </span>
                                            <span className="rental-action-dropdown-subtitle">
                                              {rental.status === RentalStatus.EN_ATTENTE || rental.status === RentalStatus.EN_PREPARATION || rental.status === RentalStatus.A_LIVRER
                                                ? "Changer les dates de début et fin"
                                                : isAdmin
                                                  ? "Changer les dates de début et fin"
                                                  : "Faire une demande de fin de location"}
                                            </span>
                                          </div>
                                        </button>
                                      )}
                                      {isAdmin && (rental.status ===
                                        RentalStatus.EN_ATTENTE ||
                                        rental.status ===
                                          RentalStatus.EN_PREPARATION ||
                                        rental.status ===
                                          RentalStatus.A_LIVRER ||
                                        rental.status ===
                                          RentalStatus.ACTIF) && (
                                        <button
                                          className="action-dropdown-item modify-action"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenInternalRefModal(rental);
                                            onCloseAllDropdowns();
                                          }}
                                        >
                                          <i className="fas fa-tag rental-action-dropdown-icon-modify"></i>
                                          <div className="rental-action-dropdown-content">
                                            <span className="rental-action-dropdown-title">
                                              Modifier la reference
                                            </span>
                                            <span className="rental-action-dropdown-subtitle">
                                              Changer la reference interne
                                            </span>
                                          </div>
                                        </button>
                                      )}
                                      {isAdmin && (rental.status ===
                                        RentalStatus.EN_ATTENTE ||
                                        rental.status ===
                                          RentalStatus.EN_PREPARATION ||
                                        rental.status ===
                                          RentalStatus.A_LIVRER ||
                                        rental.status ===
                                          RentalStatus.ACTIF) && (
                                        <button
                                          className="action-dropdown-item modify-action"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenPriceModal(rental);
                                            onCloseAllDropdowns();
                                          }}
                                        >
                                          <i className="fas fa-euro-sign rental-action-dropdown-icon-modify"></i>
                                          <div className="rental-action-dropdown-content">
                                            <span className="rental-action-dropdown-title">
                                              Modifier le prix
                                            </span>
                                            <span className="rental-action-dropdown-subtitle">
                                              Changer le prix journalier
                                            </span>
                                          </div>
                                        </button>
                                      )}
                                      {rental.status ===
                                        RentalStatus.EN_ATTENTE &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item validate-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenValidationModal(rental, false, false, true);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-check-circle rental-action-dropdown-icon-validate"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Mettre en préparation
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Passer en préparation
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {rental.status ===
                                        RentalStatus.EN_PREPARATION &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item validate-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenValidationModal(rental);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-box rental-action-dropdown-icon-validate"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Passer à livrer
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Valider et passer en attente de livraison
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {rental.status ===
                                        RentalStatus.A_LIVRER &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item validate-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenValidationModal(rental, true);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-truck rental-action-dropdown-icon-validate"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Marquer comme livré
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Passer en actif après livraison
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {(rental.status ===
                                        RentalStatus.EN_ATTENTE ||
                                        rental.status ===
                                          RentalStatus.EN_PREPARATION) &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                        <button
                                          className="action-dropdown-item cancel-action"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenDeleteModal(rental);
                                            onCloseAllDropdowns();
                                          }}
                                        >
                                          <i className="fas fa-trash rental-action-dropdown-icon-delete"></i>
                                          <div className="rental-action-dropdown-content">
                                            <span className="rental-action-dropdown-title">
                                              Supprimer
                                            </span>
                                            <span className="rental-action-dropdown-subtitle">
                                              Supprimer cette location
                                            </span>
                                          </div>
                                        </button>
                                      )}
                                      {rental.status ===
                                        RentalStatus.DATE_DEPASSEE &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item extend-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenExtendModal(rental);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-calendar-plus rental-action-dropdown-icon-extend"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Prolonger
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Étendre la durée de location
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {(rental.status === RentalStatus.ACTIF ||
                                        rental.status ===
                                          RentalStatus.DATE_DEPASSEE) &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item terminate-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenTerminateModal(rental);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-stop-circle rental-action-dropdown-icon-terminate"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Terminer la location
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Terminer définitivement
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {rental.status ===
                                        RentalStatus.ATTENTE_FIN && (
                                          <>
                                            {userRole === Roles.ADMIN ||
                                            userRole === Roles.SALES_REP ? (
                                              <>
                                                <button
                                                  className="action-dropdown-item validate-action"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    onOpenTerminateModal(rental);
                                                    onCloseAllDropdowns();
                                                  }}
                                                >
                                                  <i className="fas fa-check-circle rental-action-dropdown-icon-validate"></i>
                                                  <div className="rental-action-dropdown-content">
                                                    <span className="rental-action-dropdown-title">
                                                      Approuver la demande
                                                    </span>
                                                    <span className="rental-action-dropdown-subtitle">
                                                      Terminer la location à la date demandée
                                                    </span>
                                                  </div>
                                                </button>
                                                <button
                                                  className="action-dropdown-item modify-action"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    onModifyRental(rental);
                                                    onCloseAllDropdowns();
                                                  }}
                                                >
                                                  <i className="fas fa-calendar-alt rental-action-dropdown-icon-modify"></i>
                                                  <div className="rental-action-dropdown-content">
                                                    <span className="rental-action-dropdown-title">
                                                      Modifier les dates
                                                    </span>
                                                    <span className="rental-action-dropdown-subtitle">
                                                      Changer les dates de début et fin
                                                    </span>
                                                  </div>
                                                </button>
                                              </>
                                            ) : (
                                              <button
                                                className="action-dropdown-item cancel-action"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  onCancelEndRequest(rental);
                                                  onCloseAllDropdowns();
                                                }}
                                              >
                                                <i className="fas fa-times-circle rental-action-dropdown-icon-delete"></i>
                                                <div className="rental-action-dropdown-content">
                                                  <span className="rental-action-dropdown-title">
                                                    Annuler la demande
                                                  </span>
                                                  <span className="rental-action-dropdown-subtitle">
                                                    Revenir au statut actif
                                                  </span>
                                                </div>
                                              </button>
                                            )}
                                          </>
                                        )}
                                      {rental.status ===
                                        RentalStatus.A_RECUPERER &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item validate-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenRecoveryModal(rental);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-box-open rental-action-dropdown-icon-validate"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Marquer comme récupéré
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Confirmer la récupération de l'equipement
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                      {/* Bouton supprimer pour admin/sales - tous les statuts sauf EN_ATTENTE et EN_PREPARATION (déjà gérés) */}
                                      {rental.status !== RentalStatus.EN_ATTENTE &&
                                        rental.status !== RentalStatus.EN_PREPARATION &&
                                        (userRole === Roles.ADMIN ||
                                          userRole === Roles.SALES_REP) && (
                                          <button
                                            className="action-dropdown-item cancel-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenDeleteModal(rental);
                                              onCloseAllDropdowns();
                                            }}
                                          >
                                            <i className="fas fa-trash rental-action-dropdown-icon-delete"></i>
                                            <div className="rental-action-dropdown-content">
                                              <span className="rental-action-dropdown-title">
                                                Supprimer
                                              </span>
                                              <span className="rental-action-dropdown-subtitle">
                                                Supprimer définitivement cette location
                                              </span>
                                            </div>
                                          </button>
                                        )}
                                    </div>
                                  );
                                })()}
                            </div>
                          ) : (
                            <div className="rental-action-placeholder"></div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination */}
            {rentals.length > this.itemsPerPage && (
              <div className="d-flex justify-content-center mt-3 mb-3">
                <CustomPagination
                  activePage={currentPage}
                  itemsCountPerPage={this.itemsPerPage}
                  totalItemsCount={rentals.length}
                  pageRangeDisplayed={5}
                  onChange={this.handlePageChange}
                  variant="primary"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default RentalSectionTable;
